/**
 * FunctionsView Component
 * 
 * Unified list view of all cloud code items (functions, triggers, jobs).
 * Features:
 * - Expandable rows to view code
 * - Filter by type (function, trigger, job)
 * - Search by name
 * - Test button for functions
 * - Copy endpoint functionality
 */

import { useState, useCallback, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import { CloudCodeItem, CloudCodeType } from '../../../../types/AppPanel';
import FunctionTestConsole from './FunctionTestConsole';

interface FunctionsViewProps {
  /** Unified list of cloud code items */
  items: CloudCodeItem[];
  /** Parse Server URL for testing */
  parseServerURL?: string;
  /** Application ID for testing */
  appId?: string;
  /** Master Key for testing */
  masterKey?: string;
}

export default function FunctionsView({
  items,
  parseServerURL,
  appId,
  masterKey,
}: FunctionsViewProps) {
  // Filter state
  const [typeFilter, setTypeFilter] = useState<CloudCodeType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Expanded rows
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  
  // Test console state
  const [testingFunction, setTestingFunction] = useState<string | null>(null);
  
  // Copy feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Toggle row expansion
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Copy endpoint
  const copyEndpoint = useCallback((item: CloudCodeItem) => {
    if (item.endpoint && parseServerURL) {
      navigator.clipboard.writeText(`${parseServerURL}${item.endpoint}`);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, [parseServerURL]);

  // Filter items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Type filter
      if (typeFilter !== 'all' && item.type !== typeFilter) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = item.name.toLowerCase().includes(query);
        const targetMatch = item.targetClass?.toLowerCase().includes(query);
        const fileMatch = item.filePath.toLowerCase().includes(query);
        return nameMatch || targetMatch || fileMatch;
      }
      return true;
    });
  }, [items, typeFilter, searchQuery]);

  // Count by type
  const counts = useMemo(() => ({
    all: items.length,
    function: items.filter(i => i.type === 'function').length,
    trigger: items.filter(i => i.type === 'trigger').length,
    job: items.filter(i => i.type === 'job').length,
  }), [items]);

  // No items
  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-white/30">
            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p className="text-white/40 text-sm text-center">
          No cloud code defined yet.<br />
          Functions, triggers, and jobs will appear here once deployed.
        </p>
      </div>
    );
  }

  const canTest = parseServerURL && appId;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Filter Bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-black/20 border-b border-white/5">
        {/* Type filters */}
        <div className="flex items-center gap-1">
          <FilterButton
            active={typeFilter === 'all'}
            onClick={() => setTypeFilter('all')}
            count={counts.all}
          >
            All
          </FilterButton>
          <FilterButton
            active={typeFilter === 'function'}
            onClick={() => setTypeFilter('function')}
            count={counts.function}
            icon={<FunctionIcon />}
          >
            Functions
          </FilterButton>
          <FilterButton
            active={typeFilter === 'trigger'}
            onClick={() => setTypeFilter('trigger')}
            count={counts.trigger}
            icon={<TriggerIcon />}
          >
            Triggers
          </FilterButton>
          {counts.job > 0 && (
            <FilterButton
              active={typeFilter === 'job'}
              onClick={() => setTypeFilter('job')}
              count={counts.job}
              icon={<JobIcon />}
            >
              Jobs
            </FilterButton>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Filter..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-40 px-3 py-1.5 pl-8 rounded-md
              bg-white/5 border border-white/10
              text-sm text-white placeholder-white/30
              focus:outline-none focus:border-white/20
              transition-colors
            "
          />
          <svg 
            width="14" 
            height="14" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
      </div>

      {/* Table Header */}
      <div className="flex items-center gap-3 px-4 py-2 bg-black/10 border-b border-white/5 text-xs font-medium text-white/40 uppercase tracking-wider">
        <div className="w-5" /> {/* Expand chevron */}
        <div className="w-[220px]">Name</div>
        <div className="w-20">Type</div>
        <div className="w-28">Target</div>
        <div className="flex-1 min-w-[180px]">Endpoint / File</div>
        <div className="w-20 text-right">Actions</div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-auto">
        {filteredItems.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-white/40 text-sm">
            No items match your filter
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredItems.map((item) => (
              <CloudCodeRow
                key={item.id}
                item={item}
                isExpanded={expandedIds.has(item.id)}
                onToggle={() => toggleExpand(item.id)}
                onTest={canTest ? () => setTestingFunction(item.name) : undefined}
                onCopy={() => copyEndpoint(item)}
                isCopied={copiedId === item.id}
                parseServerURL={parseServerURL}
              />
            ))}
          </div>
        )}
      </div>

      {/* Test Console Modal */}
      {testingFunction && parseServerURL && appId && (
        <FunctionTestConsole
          functionName={testingFunction}
          parseServerURL={parseServerURL}
          appId={appId}
          masterKey={masterKey}
          isOpen={true}
          onClose={() => setTestingFunction(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// CloudCodeRow - Individual row with expand functionality
// ============================================================================

interface CloudCodeRowProps {
  item: CloudCodeItem;
  isExpanded: boolean;
  onToggle: () => void;
  onTest?: () => void;
  onCopy: () => void;
  isCopied: boolean;
  parseServerURL?: string;
}

function CloudCodeRow({
  item,
  isExpanded,
  onToggle,
  onTest,
  onCopy,
  isCopied,
  parseServerURL,
}: CloudCodeRowProps) {
  // Display name: for functions show the name, for triggers show the trigger type
  const displayName = item.type === 'trigger' ? item.triggerType : item.name;
  
  return (
    <div className="group">
      {/* Main Row */}
      <div 
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] cursor-pointer transition-colors"
        onClick={onToggle}
      >
        {/* Expand chevron */}
        <button className="w-5 h-5 flex items-center justify-center text-white/40 hover:text-white/70 flex-shrink-0">
          <svg 
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* Name - increased width */}
        <div className="w-[220px] flex items-center gap-2 flex-shrink-0">
          <TypeIcon type={item.type} triggerType={item.triggerType} />
          <code className="text-sm font-mono text-white font-medium truncate" title={displayName}>
            {displayName}
          </code>
        </div>

        {/* Type */}
        <div className="w-20 flex-shrink-0">
          <TypeBadge type={item.type} />
        </div>

        {/* Target (for triggers) */}
        <div className="w-28 flex-shrink-0">
          {item.targetClass ? (
            <code className="text-sm font-mono text-[#208AEC]">{item.targetClass}</code>
          ) : (
            <span className="text-white/20">—</span>
          )}
        </div>

        {/* Endpoint / File - show endpoint for functions, file for others */}
        <div className="flex-1 min-w-[180px] min-w-0">
          {item.type === 'function' && item.endpoint ? (
            <code className="text-xs text-emerald-400/80 font-mono truncate block" title={item.endpoint}>
              POST {item.endpoint}
            </code>
          ) : (
            <span className="text-xs text-white/40 font-mono truncate block" title={item.filePath}>
              {item.filePath}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="w-20 flex items-center justify-end gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {item.type === 'function' && (
            <>
              {/* Copy endpoint button with clearer label */}
              <button
                onClick={onCopy}
                className={`
                  flex items-center gap-1 px-1.5 py-1 rounded text-xs
                  transition-colors
                  ${isCopied 
                    ? 'text-emerald-400 bg-emerald-400/10' 
                    : 'text-white/40 hover:text-white/70 hover:bg-white/10'}
                `}
                title={`Copy endpoint: ${parseServerURL}${item.endpoint}`}
              >
                {isCopied ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  </>
                )}
              </button>

              {/* Test button */}
              {onTest && (
                <button
                  onClick={onTest}
                  className="
                    flex items-center gap-1 px-2 py-1 rounded-md
                    text-xs text-[#208AEC] font-medium
                    bg-[#208AEC]/10 hover:bg-[#208AEC]/20
                    border border-[#208AEC]/20 hover:border-[#208AEC]/40
                    transition-all duration-200
                  "
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  Run
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 bg-black/20">
          <div className="ml-6 space-y-3">
            {/* Endpoint info */}
            {item.endpoint && parseServerURL && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-white/40">Endpoint:</span>
                <code className="text-white/70 font-mono">
                  POST {parseServerURL}{item.endpoint}
                </code>
              </div>
            )}

            {/* File path */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-white/40">File:</span>
              <code className="text-white/70 font-mono">{item.filePath}</code>
            </div>

            {/* Code preview */}
            <div className="rounded-lg overflow-hidden border border-white/10">
              <Editor
                height={Math.min(Math.max(item.code.split('\n').length * 19, 100), 300)}
                language="javascript"
                value={item.code}
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  fontSize: 12,
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  padding: { top: 8, bottom: 8 },
                  scrollbar: { verticalScrollbarSize: 8 },
                  folding: true,
                  glyphMargin: false,
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 3,
                  renderLineHighlight: 'none',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

function FilterButton({
  active,
  onClick,
  count,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium
        transition-all duration-200
        ${active
          ? 'bg-white/10 text-white'
          : 'text-white/50 hover:text-white/70 hover:bg-white/5'
        }
      `}
    >
      {icon}
      {children}
      <span className={`ml-0.5 ${active ? 'text-white/60' : 'text-white/30'}`}>
        {count}
      </span>
    </button>
  );
}

function TypeBadge({ type }: { type: CloudCodeType }) {
  const config = {
    function: { label: 'Function', color: 'bg-[#208AEC]/15 text-[#208AEC]' },
    trigger: { label: 'Trigger', color: 'bg-amber-500/15 text-amber-400' },
    job: { label: 'Job', color: 'bg-purple-500/15 text-purple-400' },
  };

  const { label, color } = config[type];

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function TypeIcon({ type, triggerType }: { type: CloudCodeType; triggerType?: string }) {
  if (type === 'function') {
    return (
      <div className="w-6 h-6 rounded-md bg-[#208AEC]/15 flex items-center justify-center flex-shrink-0">
        <FunctionIcon />
      </div>
    );
  }

  if (type === 'trigger') {
    const triggerColors: Record<string, string> = {
      beforeSave: 'bg-blue-500/15 text-blue-400',
      afterSave: 'bg-emerald-500/15 text-emerald-400',
      beforeDelete: 'bg-red-500/15 text-red-400',
      afterDelete: 'bg-orange-500/15 text-orange-400',
      beforeFind: 'bg-purple-500/15 text-purple-400',
      afterFind: 'bg-violet-500/15 text-violet-400',
      beforeLogin: 'bg-cyan-500/15 text-cyan-400',
      afterLogin: 'bg-teal-500/15 text-teal-400',
    };

    return (
      <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${triggerColors[triggerType || ''] || 'bg-amber-500/15 text-amber-400'}`}>
        <TriggerIcon />
      </div>
    );
  }

  return (
    <div className="w-6 h-6 rounded-md bg-purple-500/15 flex items-center justify-center flex-shrink-0">
      <JobIcon />
    </div>
  );
}

function FunctionIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-[#208AEC]">
      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function TriggerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-400">
      <path d="M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21S18 15 18 8Z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function JobIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-400">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}
